import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Brain, Users, Gauge, Target, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = '/api';

const badgeClass = (level) => {
  switch (level) {
    case 'Prioridade Máxima':
      return 'bg-purple-100 text-purple-800';
    case 'Alta Prioridade':
      return 'bg-emerald-100 text-emerald-800';
    case 'Prioridade Moderada':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const formatStatus = (status) => {
  if (status === 'baseline') return 'Sem histórico suficiente';
  if (status === 'em_avaliacao') return 'Em desenvolvimento';
  if (status === 'oficial') return 'IPC oficial';
  return 'Não informado';
};

const Bar = ({ label, value, weight }) => {
  const safe = Number(value || 0);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold text-slate-800">
          {safe.toFixed(1)}
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-slate-800 rounded-full" style={{ width: `${Math.min(100, Math.max(0, safe))}%` }} />
      </div>
    </div>
  );
};

const MDIExplain = () => {
  const [rows, setRows] = useState([]);
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDecision = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/decision-recommendation`, { withCredentials: true });
      setRecommendation(data?.recommended || null);
      setRows(data?.ranking || []);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar Distribuição Inteligente');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDecision();
  }, []);

  const totals = rows.reduce((acc, item) => {
    acc.active += item.active_leads || 0;
    return acc;
  }, { active: 0 });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Orkestry Intelligence — Distribuição Inteligente de Leads
            </h1>
            <p className="text-slate-600 mt-1">
              Powered by Orkestry Decision Engine — Simulação auditável baseada em mérito, equilíbrio e capacidade operacional.
            </p>
          </div>
          <a href="/performance-comercial" className="px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800">
            Voltar ao IPC
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <Brain size={28} className="text-slate-700 mb-3" />
            <p className="text-sm text-slate-500">Engine</p>
            <h3 className="text-2xl font-bold text-slate-900">DMB Core</h3>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <Users size={28} className="text-slate-700 mb-3" />
            <p className="text-sm text-slate-500">Corretores elegíveis</p>
            <h3 className="text-2xl font-bold text-slate-900">{rows.length}</h3>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <Gauge size={28} className="text-slate-700 mb-3" />
            <p className="text-sm text-slate-500">Atendimentos ativos</p>
            <h3 className="text-2xl font-bold text-slate-900">{totals.active}</h3>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <Target size={28} className="text-slate-700 mb-3" />
            <p className="text-sm text-slate-500">Status</p>
            <h3 className="text-2xl font-bold text-slate-900">Simulação</h3>
          </div>
        </div>

        {recommendation && (
          <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-sm">
            <p className="text-sm text-indigo-100 mb-2">★★★★★ RECOMENDAÇÃO DA IA</p>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h2 className="text-3xl font-bold">{recommendation.broker_name}</h2>
                <p className="text-indigo-100 mt-2 max-w-4xl">
                  {recommendation.broker_name} é o corretor mais indicado para receber o próximo lead neste momento.
                  A recomendação considera mérito comercial, equilíbrio de oportunidades, recência, carga operacional e nível de confiança.
                </p>
              </div>
              <div className="text-left md:text-right">
                <p className="text-sm text-indigo-100">Nível de Confiança</p>
                <p className="text-4xl font-bold">{Math.round(recommendation.confidence_score ?? recommendation.decision_score ?? 0)}</p>
                <span className="inline-flex mt-2 px-3 py-1 rounded-full bg-white/20 text-white text-xs font-semibold">
                  {recommendation.confidence_level || recommendation.decision_level}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-5">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-indigo-100">MDI</p>
                <p className="font-bold">{recommendation.mdi_score}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-indigo-100">IPC Comercial</p>
                <p className="font-bold">{recommendation.ipc_reference ?? '--'}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-indigo-100">Carga operacional</p>
                <p className="font-bold">{recommendation.active_leads || 0} leads</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-indigo-100">Leads hoje</p>
                <p className="font-bold">{recommendation.leads_today || 0}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-indigo-100">Equilíbrio</p>
                <p className="font-bold">{recommendation.opportunity_balance?.status || 'Equilibrado'}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h2 className="font-bold text-slate-900">Ranking de Prioridade para Distribuição de Leads</h2>
            <p className="text-sm text-slate-500">
              Classificação gerada pelo Orkestry Intelligence considerando IPC Comercial, equilíbrio operacional, recência e capacidade.
            </p>
          </div>

          {loading ? (
            <div className="p-6 text-slate-500">Carregando distribuição inteligente...</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((item, index) => (
                <div key={item.broker_id} className="p-5 grid grid-cols-1 lg:grid-cols-12 gap-5">
                  <div className="lg:col-span-3">
                    <div className="text-sm text-slate-500">
                      {index === 0 ? '🥇 Recomendação da IA' : index === 1 ? 'Alternativa 1' : index === 2 ? 'Alternativa 2' : `Alternativa ${index}`}
                    </div>
                    <h3 className="font-bold text-slate-900 text-lg">{item.broker_name}</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Carga operacional: {item.active_leads || 0} lead{(item.active_leads || 0) === 1 ? '' : 's'} ativo{(item.active_leads || 0) === 1 ? '' : 's'}
                    </p>
                    <p className="text-sm text-slate-500">
                      IPC Comercial: {item.ipc_reference ?? '--'} · {formatStatus(item.ipc_status)}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Equilíbrio: {item.opportunity_balance?.status || 'Equilibrada'}
                    </p>
                  </div>

                  <div className="lg:col-span-2 flex flex-col items-start lg:items-center justify-center">
                    <div className="text-xs text-slate-500">Nível de Confiança</div>
                    <div className="text-3xl font-bold text-slate-900">{Math.round(item.confidence_score ?? item.decision_score ?? 0)}</div>
                    <div className="w-full h-2 bg-slate-100 rounded-full mt-2 overflow-hidden">
                      <div
                        className="h-full bg-slate-900 rounded-full"
                        style={{ width: `${Math.min(100, Math.max(0, item.confidence_score ?? item.decision_score ?? 0))}%` }}
                      />
                    </div>
                    <span className={`mt-2 px-3 py-1 rounded-full text-xs font-semibold ${badgeClass(item.decision_level)}`}>
                      {item.confidence_level || item.decision_level}
                    </span>
                    
                  </div>

                  <div className="lg:col-span-4 space-y-3">
                    <Bar label="IPC Comercial" value={item.decision_components?.merit} weight={item.decision_weights?.merit} />
                    <Bar label="Equilíbrio operacional" value={item.decision_components?.balance} weight={item.decision_weights?.balance} />
                    <Bar label="Recência" value={item.decision_components?.recency} weight={item.decision_weights?.recency} />
                    <Bar label="Carga operacional" value={item.decision_components?.load} weight={item.decision_weights?.load} />
                  </div>

                  <div className="lg:col-span-3 bg-slate-50 rounded-lg p-4">
                    <p className="text-xs font-bold text-slate-600 mb-2">Por que foi recomendado?</p>
                    <ul className="space-y-1">
                      {(item.decision_explain || []).map((line, i) => (
                        <li key={i} className="text-xs text-slate-600">✓ {line}</li>
                      ))}
                    </ul>
                    {index > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500">
                        Alternativa considerada pela IA, porém com nível de confiança inferior à recomendação principal neste momento.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MDIExplain;
